import ContainerSpinner from '@app/components/Container/ContainerContent/ContainerSpinner'
import type { IContainerFlexProps } from '@app/components/Container/ContainerFlex/types'
import { ErrorMessage, ErrorRbacDenyMessage } from '@app/components/Error'
import { Label } from '@app/components/Labels'
import { LabelVariant } from '@app/components/Labels/types'
import type { IComponentLoaderProps } from '@app/components/types'
import ContainerOpacity from '@app/components-legacy/Container/ContainerOpacity'
import SpinnerLogo from '@app/components-legacy/Spinner/SpinnerLogo'
import { useLoader } from '@app/hooks/useLoader'
import { useStores } from '@app/hooks/useStores'
import { mergeFlags } from '@app/stores/helpers/StoreFlags/helpers'
import { useDSTheme } from '@design-system/hooks/useDSTheme'
import { isThemeLegacy } from '@design-system/styles/themes/helpers'
import { ensureArray } from '@libs/ensureArray'
import type { RbacCapabilityCheck } from '@libs/rbac/types'
import { computeComponentName } from '@libs/react-helpers/computeComponentName'
import React from 'react'
import styled from 'styled-components'
import { ContainerFlex, ContainerFlexLegacy } from '../index'

interface IContainerContentProps extends IComponentLoaderProps {
  name?: string
  labelledBy?: string
  style?: React.CSSProperties
  className?: string
  title?: React.ReactNode
  fixedHeight?: number
  fullHeight?: boolean
  fullWidth?: boolean
  spinner?: React.ReactNode
  children: React.ReactNode
  rbacCapabilityCheck?: RbacCapabilityCheck
  spaceWidth?: IContainerFlexProps['spaceWidth']
  onError?: () => React.ReactNode
  onDeny?: () => React.ReactNode
}

const StyledSubTitle = styled(Label)`
  display: block;
  margin-bottom: 30px;
`

/**
 * Container that allows to:
 * - display a spinner according to IFlags object,
 * - display an error
 * - display a title
 * - display children
 */
const ContainerContent: React.FC<IContainerContentProps> = props => {
  useLoader(props)

  const { storeRbac } = useStores()

  const theme = useDSTheme()

  const flags = mergeFlags(...ensureArray(props.flags))

  const renderError = () => {
    if (props.onError) {
      return props.onError()
    }

    return <ErrorMessage />
  }

  const renderDeny = () => {
    if (props.onDeny) {
      return props.onDeny()
    }

    return <ErrorRbacDenyMessage />
  }

  const getPreloadItems = () => {
    if (flags.isLoading) {
      return (
        <ContainerSpinner>{props.spinner || <SpinnerLogo />}</ContainerSpinner>
      )
    }

    if (flags.isError) {
      return renderError()
    }

    if (flags.isForbidden) {
      return renderDeny()
    }

    const isDenied =
      props.rbacCapabilityCheck &&
      !storeRbac.isUserGrantedTo(props.rbacCapabilityCheck)

    if (isDenied) {
      return renderDeny()
    }

    return null
  }

  const fullHeight = props.fullHeight ?? true
  const fullWidth = props.fullWidth
  const preLoadItems = getPreloadItems()

  if (preLoadItems) {
    return (
      <ContainerFlex
        name={computeComponentName('ContainerContent', props.name)}
        style={props.style}
        className={props.className}
        // display error at the top of the page,
        alignItems={flags.isError ? 'flex-start' : 'center'}
        justifyContent={flags.isLoading ? 'center' : 'flex-start'}
        items={[preLoadItems]}
        fullHeight={fullHeight}
        fullWidth={fullWidth}
      />
    )
  }

  return (
    <ContainerOpacity
      name={computeComponentName('ContainerContent', props.name)}
      labelledBy={props.labelledBy}
      className={props.className}
      style={props.style}
      fullHeight={fullHeight}
      fullWidth={fullWidth}
      fade={props.fade ?? true}
    >
      <ContainerFlexLegacy
        name="ContainerFlexLegacy"
        items={[
          props.title && (
            <div>
              {isThemeLegacy(theme) ? (
                <StyledSubTitle variant={LabelVariant.subtitle1}>
                  {props.title}
                </StyledSubTitle>
              ) : (
                <Label variant={LabelVariant.h2}>{props.title}</Label>
              )}
            </div>
          ),
          ...React.Children.toArray(props.children)
        ]}
        spaced
        spaceWidth={props.spaceWidth || 'default'}
        fullHeight={fullHeight}
        fullWidth={fullWidth}
      />
    </ContainerOpacity>
  )
}

export default styled(ContainerContent)`
  ${props => (props.fixedHeight ? `min-height: ${props.fixedHeight}px` : '')};
  ${props => (props.fixedHeight ? `max-height: ${props.fixedHeight}px` : '')};
`
